'use client'
// CLIENT BOUNDARY, and the only one in the chrome. The menu is a modal
// disclosure: it opens, holds focus, closes on Escape, and closes on a route
// change. Every one of those is visitor-caused state, which a server component
// cannot hold. V3Footer and V3Breadcrumb stay pure server components.
//
// The BAR needs nothing from the client and would render as RSC if this unit
// owned a second file to put the menu in. It does not: the unit is
// V3Chrome.tsx, V3Footer.tsx, V3Breadcrumb.tsx and their three stylesheets. So
// the bar rides inside this boundary, where it is markup and links only. It
// server-renders to HTML like any client component, so every destination below
// is in the initial response for a crawler and for a visitor with no JS; only
// the menu's open state and the caret panels need hydration. Lifting the bar
// into its own server file later is a file move, not a rewrite.
/**
 * V3 CHROME. The public site's persistent header.
 *
 * Replaces a deleted KB component Visual language:
 * design_system/public/PUBLIC_UI.md (locked 2026-08-11). Tokens: ./tokens.css.
 *
 * DESTINATIONS COME FROM lib/site-nav.ts, NEVER FROM THIS FILE.
 * KB_TOP_NAV and KB_MENU_GROUPS are read at module load and projected into one
 * model. Not one href is typed here, so a link cannot drift out of the single
 * source of truth by being copied: the only two literal destinations in this
 * file are `/` on the wordmark and the tel: URI built from lib/brand/contact.
 * A development-time audit at the bottom of the model proves the projection
 * lost nothing: every href in either projection is reachable in the chrome, and
 * it warns by name if one is not.
 *
 * LABELS COME FROM THE IA LOCK (docs/plans/PUBLIC_PRODUCT/ia-lock.md).
 * Since Matt's 2026-10-25 menu redesign the words are Buy, Sell, Places,
 * Market, About. Saved / Sign in are account affordances rather than nav words.
 * Where a locked word lands on an existing group the group is RENAMED, never
 * rebuilt: Areas renders as Places, each keeping its own href and its own children. A
 * group the lock has no word for is CARRIED under its own label and warned
 * about in development, because dropping it would delete destinations, which is
 * the one thing this rebuild may not do.
 *
 * ONE FILLED ASK on Sell leaves that do not already own the form submit
 * (chromeShowsSellerAsk): FSBO/expired fill chrome; /sell and /sell/valuation
 * keep the form as the only filled Value my home. Buyer/place/market/about/
 * listing chrome never fill it. The door stays in the Sell nav group. The
 * footer never carries a second solid button (PUBLIC_UI.md section 1).
 *
 * WORK WITH US IS OUT OF THE CHROME (Matt 2026-09-21, SITE-155). The
 * 2026-09-19 CTA lock put an outline V3WorkWithUs trigger in the header at
 * every width; Matt reversed that: "There's a 'Work with us' button that
 * needs to be removed. We're going to use the dog for that." V3DogFloater
 * (SITE-153, mounted once in app/layout.tsx) is that door — do not re-add a
 * chrome trigger. The V3WorkWithUs sheet itself is untouched and still
 * opens from the listing agent card.
 *
 * SEARCH: catalog MorphingSearch via V3ChromeSearch (beUI install). Client
 * open, ⌘K / Ctrl+K, icon on phone / pill on desk. Suggest feed for places —
 * not a second token layer or a house-invented chrome field.
 *  - The transparent-over-hero bar with a scroll listener that flipped it to
 *    solid. One bar, one color contract, legible over whatever scrolls under
 *    it. AA on every text pair is a foundation, not a scroll position.
 *
 * MOUNTING: the header puts V3_ROOT_CLASS on its own outermost element, so
 * ./tokens.css resolves with no wrapper. It is `position: sticky`, so it holds
 * its own space in flow and a page needs no spacer under it.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import type { AuthUser } from '@/lib/auth/types'
import { loadSessionUser } from '@/lib/auth/session-user.client'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { CONTACT } from '@/lib/brand/contact'
import {
  KB_MENU_GROUPS,
  KB_TOP_NAV,
  VALUATION_FORM,
  type NavLink,
} from '@/lib/site-nav'
import { valuationHref } from '@/lib/site/valuation-href'
import { chromeShowsSellerAsk } from '@/lib/site/chrome-seller-ask'
import { chromeMegaColumnMarks, chromeMegaModel } from '@/lib/site/chrome-mega'
import { shouldHidePublicChrome } from '@/lib/site/public-chrome-hide'
import { V3Button, V3_ROOT_CLASS, v3Text, type V3Text } from './atoms'
import {
  chromeCaretPress,
  chromeLinkMove,
  chromeMenuStep,
  chromePointerHovers,
  historyStepMoves,
  type ChromePanelOpenedBy,
} from './V3Chrome.logic'
import { V3ChromeSearch } from './V3ChromeSearch.client'
import { useIntentPrefetch } from './use-intent-prefetch'
import { V3Icon } from './V3Icon'
import './tokens.css'
import './V3Chrome.css'

/* -------------------------------------------------------------------------- */
/* The model: site-nav.ts projected onto the locked IA words                    */
/* -------------------------------------------------------------------------- */

/**
 * The locked destination words (ia-lock.md), keyed by the site-nav group they
 * rename. Typed with `| undefined` on purpose: a group this map has no word for
 * is a real case (site-nav.ts may grow one), and the code below has to be able
 * to ask whether a key is missing.
 */
const LOCKED_LABEL: Readonly<Record<string, string | undefined>> = {
  // Matt 2026-10-25 (menu redesign A): the bar reads Buy / Sell / Places /
  // Market / About, so the buyer door says Buy again.
  Buy: 'Buy',
  Areas: 'Places',
  Market: 'Market',
  Sell: 'Sell',
  About: 'About',
  'Your account': 'Saved',
}

/** The site-nav group whose destination is the visitor's own saved work. */
const ACCOUNT_KEY = 'Your account'

/** Primary bar destinations (Buy · Sell · Places · Market · About). */
const PRIMARY_BAR_KEYS = new Set(['Buy', 'Areas', 'Market', 'Sell', 'About'])

/** Place marks for community doors in chrome menus (existing stills, not text dumps). */
const CHROME_MARKS: Readonly<Record<string, string>> = {
  '/communities/tetherow': '/images/chrome-marks/tetherow.jpg',
  '/communities/broken-top': '/images/chrome-marks/broken-top.jpg',
  '/communities/northwest-crossing': '/images/chrome-marks/northwest-crossing.jpg',
  '/communities/caldera-springs': '/images/chrome-marks/caldera-springs.jpg',
}


/** A destination group as the chrome renders it. */
export type V3ChromeGroup = {
  /** The site-nav group key, kept so a rename in the lock cannot orphan it. */
  key: string
  /** The locked IA word, or the group's own label when the lock has none. */
  label: V3Text
  /** The group's own overview page. Absent for the account group. */
  href?: string
  /** The curated set the desktop panel shows (KB_TOP_NAV children). */
  featured: readonly NavLink[]
  /** Panel set plus menu set, deduped: what the overlay shows at every width. */
  links: readonly NavLink[]
  /** One quiet door under the desktop panel (Buy: Get listing alerts). */
  foot?: NavLink
}

/** A group that is also a destination, so it can head the bar. */
type V3ChromeTopGroup = V3ChromeGroup & { href: string }

/** One live figure with the words that make it true. */
export type V3ChromeLiveFact = {
  figure: string
  label: string
  /** Words after the figure ("label figure after"), e.g. ", as of Oct 21, 2026". */
  after?: string
}

/**
 * What a group's menu says is true right now. Composed on the server
 * (lib/site/chrome-live.ts) from the DAL and the place atlas; the chrome only
 * prints it. Every figure arrives formatted, so the chrome never rounds.
 */
export type V3ChromeLiveGroup = {
  /** The scope the figures belong to: "Central Oregon right now". */
  eyebrow: string
  facts: readonly V3ChromeLiveFact[]
  /** A value beside a destination, keyed by its href: "/cities/bend" → "1,204". */
  values?: Readonly<Record<string, string>>
  /** A dot field: the listings as one path of zero-length strokes in a w×h box. */
  field?: { w: number; h: number; d: string }
  /** When the figures were read: "Read Sep 2, 2026, 12:07 AM". */
  note?: string
}

/** Live groups keyed by the site-nav group key (Buy, Areas, Market, Sell). */
export type V3ChromeLive = Readonly<Record<string, V3ChromeLiveGroup>>

/**
 * Trimmed, named, deduped. A link with no href is not a door and a link with no
 * label would ship an anchor with no accessible name (WCAG 2.4.4), so both are
 * dropped rather than rendered. The dedupe is by href and it matters: the
 * overlay merges two projections that overlap heavily, and two children with
 * the same key is a React error as well as an IA defect.
 */
function clean(links: readonly NavLink[]): NavLink[] {
  const seen = new Set<string>()
  const out: NavLink[] = []
  for (const link of links) {
    const href = link?.href?.trim()
    const label = link?.label?.trim()
    if (!href || !label || seen.has(href)) continue
    seen.add(href)
    out.push({ href, label })
  }
  return out
}

const MENU_LINKS_BY_TITLE = new Map(KB_MENU_GROUPS.map((g) => [g.title, g.links]))
const TOP_KEYS = new Set(KB_TOP_NAV.map((g) => g.label))

/**
 * The whole chrome model, built once at module load.
 *
 * The bar's five destinations are KB_TOP_NAV in its own order. The overlay adds
 * every group KB_MENU_GROUPS has that the top bar does not, which today is the
 * account group and is exactly why nothing is lost by the top bar being five
 * words wide.
 *
 * `links` merges both projections because they are NOT the same set: the top
 * bar knows `/sell/valuation` and `/homes-for-sale?status=Sold`, while the menu
 * knows eight communities the top bar trims to six. KbNav showed the top-bar
 * set only above 1000px, which left a handful of destinations unreachable on a
 * phone. Merging fixes that without adding a link anyone has to maintain.
 */
const NAV_GROUPS: readonly V3ChromeGroup[] = [
  ...KB_TOP_NAV.map((group) => ({
    key: group.label,
    label: v3Text(LOCKED_LABEL[group.label] ?? group.label),
    href: group.href,
    featured: clean(group.children),
    links: clean([...group.children, ...(MENU_LINKS_BY_TITLE.get(group.label) ?? [])]),
    foot: group.foot ? clean([group.foot])[0] : undefined,
  })),
  ...KB_MENU_GROUPS.filter((group) => !TOP_KEYS.has(group.title)).map((group) => ({
    key: group.title,
    label: v3Text(LOCKED_LABEL[group.title] ?? group.title),
    featured: [] as readonly NavLink[],
    links: clean(group.links),
  })),
]

const TOP_GROUPS: readonly V3ChromeTopGroup[] = NAV_GROUPS.filter(
  (group): group is V3ChromeTopGroup =>
    typeof group.href === 'string' && PRIMARY_BAR_KEYS.has(group.key),
)

const ACCOUNT_GROUP = NAV_GROUPS.find((group) => group.key === ACCOUNT_KEY)


/**
 * Sign in from the account group. It sits in the bar from 56.25rem up; below
 * that the bar's slot is the phone (UXLIVE-13) and Sign in is reached in the
 * menu, where the account group ("Saved") already lists it.
 */
const SIGN_IN = ACCOUNT_GROUP?.links.find((link) => link.href === '/login') ?? null

const ACCOUNT_HOME =
  ACCOUNT_GROUP?.links.find((link) => link.href === '/account') ??
  (ACCOUNT_GROUP && ACCOUNT_GROUP.links.length > 0 ? ACCOUNT_GROUP.links[0] : null)

/**
 * The filled seller ask. Only the LABEL is fixed at module scope: the href is
 * built per render by valuationHref(path) so the ask carries
 * `?from=<originating path>` into the seller action's sourceUrl. Rendered only
 * when chromeShowsSellerAsk(path) is true (Sell). KPI attribution still keys
 * off body valuation links on other routes.
 */
const CTA = { label: v3Text(VALUATION_FORM.label) }

/**
 * Fixed accessible names. Built through v3Text so a blank one throws at import
 * rather than shipping a nameless landmark or a nameless control.
 */
const NAME = {
  home: v3Text('Ryan Realty home'),
  primary: v3Text('Primary'),
  menu: v3Text('Site menu'),
  sections: v3Text('Site sections'),
  openMenu: v3Text('Menu'),
  closeMenu: v3Text('Close menu'),
}

if (process.env.NODE_ENV !== 'production') {
  const reachable = new Set<string>()
  for (const group of NAV_GROUPS) {
    if (group.href) reachable.add(group.href)
    for (const link of group.links) reachable.add(link.href)
  }
  const required = new Set<string>([
    ...KB_TOP_NAV.flatMap((g) => [g.href, ...g.children.map((c) => c.href)]),
    ...KB_MENU_GROUPS.flatMap((g) => g.links.map((l) => l.href)),
    VALUATION_FORM.href,
  ])
  const missing = [...required].filter((href) => !reachable.has(href))
  if (missing.length > 0) {
    console.warn(
      `V3Chrome: ${missing.length} site-nav destination(s) are not reachable in the chrome: ${missing.join(', ')}`,
    )
  }
  const carried = NAV_GROUPS.filter((group) => LOCKED_LABEL[group.key] === undefined)
  if (carried.length > 0) {
    console.warn(
      `V3Chrome: carrying ${carried.length} site-nav group(s) the IA lock has no word for, under their own labels: ` +
        `${carried.map((g) => g.key).join(', ')}. Give each one a locked word or accept the carry.`,
    )
  }
}

/* -------------------------------------------------------------------------- */
/* The wordmark and the icons                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The brand asset. One definition for the two places it renders (the bar and
 * the open menu's own bar), so they cannot drift into two different logos.
 *
 * Plain img, not next/image, for the reason V3Stage states for its poster: this
 * is an owned asset that must render on any path without depending on
 * image-host configuration. It is also the WORDMARK, which brand law renders
 * from the pre-rendered file and never re-typesets, so re-encoding a
 * transparent PNG down to a 20px display height buys nothing and risks the
 * alpha edge. The intrinsic dimensions are on the element, so the row reserves
 * its space and the bar does not jump when the file lands.
 */
function Wordmark() {
  return (
    /* eslint-disable-next-line @next/next/no-img-element */
    <img
      src="/images/brand/logo-horizontal-navy-transparent.png"
      alt="Ryan Realty"
      width={2271}
      height={454}
      decoding="async"
    />
  )
}

/* Icons. Stroke is currentColor, so every one inherits the token beside it. */

function IconChevron() {
  return <V3Icon name="NavArrowDown" size={16} />
}

function IconPhone() {
  return <V3Icon name="Phone" size={16} />
}

function IconMenu() {
  return <V3Icon name="Menu" size={20} />
}

function IconClose() {
  return <V3Icon name="Xmark" size={20} />
}

/* -------------------------------------------------------------------------- */
/* Current destination                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Whether a bar destination owns the page the visitor is on. Compared against
 * the path only: a group href may carry a query (`?view=map`), and a query does
 * not change which section of the graph the visitor is standing in.
 */
function isCurrentPath(pathname: string | null, href: string): boolean {
  if (!pathname || !href.startsWith('/')) return false
  const path = href.split('?')[0]
  if (path === '/') return pathname === '/'
  return pathname === path || pathname.startsWith(`${path}/`)
}


/** About is the rightmost item in the bar; end-align its panel. The rest start under their trigger, and the shift effect pulls any panel back inside the viewport. */
const MEGA_ALIGN_END = new Set(['About'])

function ChromePanelLink({
  href,
  label,
  value,
  mark,
  onClick,
}: {
  href: string
  label: string
  value?: string
  mark?: string
  onClick: () => void
}) {
  return (
    <Link href={href} className="v3-chrome__panel-link" onClick={onClick}>
      {mark ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={mark}
          alt=""
          className="v3-chrome__link-mark"
          width={28}
          height={28}
          decoding="async"
        />
      ) : null}
      <span>{label}</span>
      {value ? <span className="v3-chrome__panel-value">{value}</span> : null}
    </Link>
  )
}

/* -------------------------------------------------------------------------- */
/* One bar destination: a link, plus a disclosure for its children              */
/* -------------------------------------------------------------------------- */

/** Fired (bubbling) by a bar group whose panel just opened, so its siblings close. */
const CHROME_PANEL_OPEN_EVENT = 'v3-chrome:panel-open'

/** A bar panel's links in reading order: the stops the arrow keys move between. */
function panelLinksOf(panel: HTMLElement | null): HTMLAnchorElement[] {
  return panel ? [...panel.querySelectorAll<HTMLAnchorElement>('a[href]')] : []
}

/** Optional listing-dot field inside the shared Now column. */
function ChromeMegaField({ field }: { field: { w: number; h: number; d: string } }) {
  return (
    <svg
      className="v3-chrome__field"
      viewBox={`0 0 ${field.w} ${field.h}`}
      aria-hidden="true"
      focusable="false"
    >
      <path d={field.d} className="v3-chrome__field-dots" />
    </svg>
  )
}

/**
 * WAI-ARIA APG "disclosure navigation menu with top-level links": a real link
 * to the group's own page, plus an adjacent caret button that owns the expanded
 * state. A mouse gets hover as a bonus, never as the only way in: the caret
 * opens its panel on a click, a tap, Enter or Space, and a press on a panel the
 * mouse already hovered open keeps it open (W4, Muse site audit 2026-10-20).
 * Keyboard users tab across five link and caret pairs and expand only what
 * they want, because auto-expanding on focus would drop every child link into
 * the tab sequence. ArrowDown on the caret opens the panel if it is closed and
 * moves to its first link (ArrowUp, its last); inside the panel the arrows
 * step and wrap, Home and End jump to the ends (chromeMenuStep), and Tab still
 * walks the links in order. Escape closes and returns focus to the caret.
 * Below 900px the whole row is display:none and the overlay carries
 * navigation.
 * e2e/features/nav-keyboard.spec.ts holds all of it in a real browser, nightly.
 *
 * The panel's links are removed from the tab order when closed by
 * `visibility: hidden`, not by opacity: an invisible-but-focusable link is a
 * keyboard trap in slow motion.
 */
function V3ChromeDestination({
  group,
  currentPath,
  live,
}: {
  group: V3ChromeTopGroup
  currentPath: string
  live?: V3ChromeLiveGroup
}) {
  // The open state is stored as the path it was opened ON, and read back by
  // comparing. A panel left open across a navigation would hang over the page
  // the visitor just asked for, and this closes it BY DERIVATION: the moment
  // the path changes, `open` is false. No effect, no cascading render, and no
  // teardown anyone can forget to write. It also keeps HOW the panel opened,
  // so a press on a caret the mouse already hovered open holds the panel
  // rather than toggling it shut (chromeCaretPress, W4).
  const [opened, setOpened] = useState<{ path: string; by: ChromePanelOpenedBy } | null>(null)
  const openedBy: ChromePanelOpenedBy = opened?.path === currentPath ? opened.by : null
  const open = openedBy !== null
  const setOpenedBy = useCallback(
    (by: ChromePanelOpenedBy) => setOpened(by ? { path: currentPath, by } : null),
    [currentPath],
  )
  const rootRef = useRef<HTMLDivElement>(null)
  const caretRef = useRef<HTMLButtonElement>(null)
  const panelId = `${useId()}-panel`
  const current = isCurrentPath(currentPath, group.href)
  const mega = chromeMegaModel(group.key, group.featured, live)
  const panelRef = useRef<HTMLDivElement>(null)
  // The link an arrow key asked for while the panel was still closed. Its
  // links are visibility:hidden until the open render commits, so focus lands
  // in the layout effect after it, not in the key handler.
  const pendingFocusRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    panel.style.setProperty('--v3-chrome-mega-shift', '0px')
    if (!open) return
    const rect = panel.getBoundingClientRect()
    const pad = 16
    let shift = 0
    if (rect.left < pad) shift += pad - rect.left
    if (rect.right + shift > window.innerWidth - pad) {
      shift -= rect.right + shift - (window.innerWidth - pad)
    }
    panel.style.setProperty('--v3-chrome-mega-shift', `${Math.round(shift)}px`)
  }, [open, mega.colCount])

  useLayoutEffect(() => {
    const index = pendingFocusRef.current
    pendingFocusRef.current = null
    if (open && index !== null) panelLinksOf(panelRef.current)[index]?.focus()
  }, [open])

  // Native focusout rather than React's onBlur: collapse once focus leaves the
  // group entirely, so tabbing out of the last child closes the panel behind it.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const onFocusOut = (event: FocusEvent) => {
      if (!el.contains(event.relatedTarget as Node | null)) setOpened(null)
    }
    el.addEventListener('focusout', onFocusOut)
    return () => el.removeEventListener('focusout', onFocusOut)
  }, [])

  // ONE BAR PANEL AT A TIME (site audit 2026-10-21, 2.3). A clicked caret keeps
  // focus, and focus inside a group holds its panel through a pointer leave, so
  // hovering the next destination opened its panel on top of the clicked one
  // and both hung open until the next press. Whichever panel opens, by hover,
  // press or arrow key, now tells its siblings in the bar to close.
  useEffect(() => {
    const el = rootRef.current
    const bar = el?.parentElement
    if (!el || !bar) return
    const onSiblingOpen = (event: Event) => {
      if (event.target === el) return
      // A keyboard user reading this panel keeps a visible place: focus goes
      // back to the caret rather than staying on a link that is now hidden.
      const active = document.activeElement
      if (active && active !== caretRef.current && panelRef.current?.contains(active)) {
        caretRef.current?.focus({ preventScroll: true })
      }
      setOpened(null)
    }
    bar.addEventListener(CHROME_PANEL_OPEN_EVENT, onSiblingOpen)
    return () => bar.removeEventListener(CHROME_PANEL_OPEN_EVENT, onSiblingOpen)
  }, [])

  useEffect(() => {
    if (open) rootRef.current?.dispatchEvent(new Event(CHROME_PANEL_OPEN_EVENT, { bubbles: true }))
  }, [open])

  return (
    <div
      ref={rootRef}
      className={cn('v3-chrome__group', open && 'is-open')}
      // Pointer events, not mouse events: a tap fires compatibility mouse
      // events, and hovering a panel open on the way to the tap's own press
      // was half of why a tap could not open one (chromePointerHovers).
      onPointerEnter={(event) => {
        if (!open && chromePointerHovers(event.pointerType)) setOpenedBy('hover')
      }}
      onPointerLeave={(event) => {
        if (!chromePointerHovers(event.pointerType)) return
        // Never yank a panel out from under a keyboard user whose focus is
        // still inside it because the pointer drifted away.
        if (!rootRef.current?.contains(document.activeElement)) setOpened(null)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          if (!open) return
          event.stopPropagation()
          setOpened(null)
          // The caret sits in the stuck bar, always on screen: nothing to scroll.
          caretRef.current?.focus({ preventScroll: true })
          return
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
        // Arrows belong to the caret and the panel's links only; the group's
        // own link keeps the browser's keys.
        const links = panelLinksOf(panelRef.current)
        const from = links.indexOf(event.target as HTMLAnchorElement)
        if (from < 0 && event.target !== caretRef.current) return
        const to = chromeMenuStep(event.key, from, links.length)
        if (to === null) return
        event.preventDefault()
        if (open) {
          links[to].focus()
        } else {
          pendingFocusRef.current = to
          setOpenedBy('press')
        }
      }}
    >
      <Link
        href={group.href}
        className="v3-chrome__group-link"
        aria-current={current ? 'page' : undefined}
      >
        {group.label}
      </Link>
      <button
        ref={caretRef}
        type="button"
        className="v3-chrome__caret"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`${group.label} pages`}
        // A native button: Enter and Space arrive here as a click, so the
        // keyboard, a tap and a mouse all take this one path.
        onClick={() => setOpenedBy(chromeCaretPress(openedBy))}
      >
        <IconChevron />
      </button>
      <div
        ref={panelRef}
        className={cn(
          'v3-chrome__panel',
          'v3-chrome__panel--mega',
          MEGA_ALIGN_END.has(group.key) && 'v3-chrome__panel--end',
        )}
        id={panelId}
        style={{ '--v3-chrome-mega-cols': String(mega.colCount) } as CSSProperties}
      >
        {mega.caption ? <p className="v3-chrome__panel-caption">{mega.caption}</p> : null}
        <div className="v3-chrome__mega">
          {mega.sections.map((section) => {
            const marks = chromeMegaColumnMarks(
              section.links.map((link) => link.href),
              CHROME_MARKS,
            )
            return (
              <div key={section.heading} className="v3-chrome__mega-col">
                <p className="v3-chrome__mega-heading">{section.heading}</p>
                <ul className="v3-chrome__panel-list v3-chrome__panel-list--stacked">
                  {section.links.map((link) => (
                    <li key={link.href}>
                      <ChromePanelLink
                        href={link.href}
                        label={link.label}
                        value={live?.values?.[link.href]}
                        mark={marks?.[link.href]}
                        onClick={() => setOpened(null)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
          {mega.now ? (
            <div className="v3-chrome__mega-col" aria-label={mega.caption ?? mega.now.heading}>
              <p className="v3-chrome__mega-heading">{mega.now.heading}</p>
              {mega.now.field ? <ChromeMegaField field={mega.now.field} /> : null}
              {mega.now.facts.length > 0 ? (
                <ul className="v3-chrome__panel-list v3-chrome__panel-list--stacked">
                  {mega.now.facts.map((fact) => (
                    <li key={`${fact.figure} ${fact.label}`}>
                      <div className="v3-chrome__panel-link v3-chrome__panel-link--fact">
                        <span>{fact.label}</span>
                        <span className="v3-chrome__panel-value">
                          {fact.figure}
                          {fact.after ?? ''}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
              {mega.now.note ? <p className="v3-chrome__live-note">{mega.now.note}</p> : null}
            </div>
          ) : null}
        </div>
        {group.foot ? (
          <p className="v3-chrome__mega-foot">
            <Link href={group.foot.href} onClick={() => setOpened(null)}>
              {group.foot.label}
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* V3Chrome                                                                    */
/* -------------------------------------------------------------------------- */

export type V3ChromeProps = {
  /**
   * Overrides the path the bar marks as current. Only for a surface that
   * renders the chrome outside a router context; the default reads
   * usePathname(), which is what every real page wants.
   */
  currentPath?: string
  id?: string
  className?: string
  /** What each group's menu says is true right now; absent groups print links only. */
  live?: V3ChromeLive | null
}

function accountAriaLabel(viewer: AuthUser): string {
  const raw = viewer.user_metadata?.full_name || viewer.user_metadata?.name || viewer.email
  const name = typeof raw === 'string' ? raw.trim() : ''
  return name ? `Account: ${name}` : 'Account'
}

function accountInitial(viewer: AuthUser): string {
  const raw = viewer.user_metadata?.full_name || viewer.user_metadata?.name || viewer.email || 'A'
  const ch = raw.trim().charAt(0)
  return ch ? ch.toUpperCase() : 'A'
}

export function V3Chrome({ currentPath, id, className, live }: V3ChromeProps) {
  const [viewer, setViewer] = useState<AuthUser | null>(null)
  const [viewerReady, setViewerReady] = useState(false)
  const [avatarFailed, setAvatarFailed] = useState(false)
  useEffect(() => {
    let alive = true
    // Audit rank 57: the shared per-page-load answer, not a fetch of its own.
    void loadSessionUser().then((user) => {
      if (!alive) return
      setViewer(user)
      setAvatarFailed(false)
      setViewerReady(true)
    })
    return () => {
      alive = false
    }
  }, [])
  const pathname = usePathname()
  const path = currentPath ?? pathname ?? ''
  // The mark sits in the first viewport of every page, so a viewport prefetch
  // pulled the homepage's client JS into every page's load. It prefetches on
  // intent instead (use-intent-prefetch.ts; perf budget 2026-11-04).
  const homeIntent = useIntentPrefetch('/')
  // The menu remembers the path it was opened ON, so a route change closes it
  // BY DERIVATION rather than by an effect that sets state: `open` is simply
  // false the moment the visitor lands somewhere else. That also means the
  // teardown below (scroll lock, key handler, focus return) runs on a
  // navigation exactly as it does on Escape, through one code path.
  const [openPath, setOpenPath] = useState<string | null>(null)
  const open = openPath === path
  // Matt 2026-10-25 (menu redesign A): the phone menu opens on four actions and
  // closed groups. One group open at a time keeps the sheet short.
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const menuId = `${useId()}-menu`
  const triggerRef = useRef<HTMLButtonElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  // Closes on a same-path click too, which derivation alone cannot see: a link
  // to the page the visitor is already on changes nothing to compare against.
  const close = useCallback(() => setOpenPath(null), [])
  // A link followed out of the chrome takes focus out of it. Keyboard focus in
  // the bar drops the page's scroll padding (V3Chrome.css), and a link to an
  // anchor (/sell#get-value) scrolls its target after the click: with focus
  // still on the link, or handed back to the menu button, the target would
  // land under the bar. Which activations move the reader is
  // V3Chrome.logic.ts (chromeLinkMove); a link to the page already showing
  // moves nobody, so the menu still hands focus back to its button.
  const followedLinkRef = useRef(false)
  const onChromeClickCapture = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    const link = (event.target as Element | null)?.closest?.('a[href]')
    if (!(link instanceof HTMLAnchorElement)) return
    const move = chromeLinkMove(event, link, window.location)
    if (!move) return
    followedLinkRef.current = true
    if (move === 'anchor') link.blur()
  }, [])

  // Back, Forward and a hash change move the reader too, and the page they
  // land on may scroll to an anchor itself (/how-we-get-our-numbers does, on
  // mount and on hashchange). When the step lands on an anchor or another page
  // (historyStepMoves), focus resting in the bar leaves it first and an open
  // menu closes without handing it back; a step that keeps the page and names
  // no anchor touches nothing. The chrome mounts once and stays, so these
  // listeners run ahead of any page's own. The path is the one last rendered,
  // the page being left: the event fires before the router re-renders.
  const renderedPathRef = useRef(path)
  useEffect(() => {
    renderedPathRef.current = path
  }, [path])
  useEffect(() => {
    const release = () => {
      if (!historyStepMoves(window.location, renderedPathRef.current)) return
      const active = document.activeElement
      if (!(active instanceof HTMLElement) || !active.closest('body > .v3-chrome')) return
      followedLinkRef.current = true
      setOpenPath(null)
      active.blur()
    }
    window.addEventListener('popstate', release)
    window.addEventListener('hashchange', release)
    return () => {
      window.removeEventListener('popstate', release)
      window.removeEventListener('hashchange', release)
    }
  }, [])
  const menuHidden = open === false
  const hidden = currentPath == null && shouldHidePublicChrome(pathname)

  // Everything the open menu owns, in one effect so the teardown cannot drift
  // from the setup: the scroll lock, the focus trap, Escape, and returning
  // focus to the control that opened it.
  useEffect(() => {
    if (!open) return
    const overlay = overlayRef.current
    if (!overlay) return

    const opener = triggerRef.current
    const body = document.body
    const priorOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    followedLinkRef.current = false
    closeRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpenPath(null)
        return
      }
      if (event.key !== 'Tab') return
      const focusable = overlay.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      body.style.overflow = priorOverflow
      // Back to the button that opened the menu, without moving the page (it
      // sits in the stuck bar). Not after a followed link: the reader is going
      // somewhere else, and focus parked in the bar would take the page's
      // scroll padding away while the destination scrolls to its anchor.
      if (!followedLinkRef.current) opener?.focus({ preventScroll: true })
      followedLinkRef.current = false
    }
  }, [open])

  if (hidden) return null

  return (
    <header id={id} className={cn(V3_ROOT_CLASS, 'v3-chrome', className)} onClickCapture={onChromeClickCapture}>
      <div className="v3-chrome__bar">
        <Link href="/" className="v3-chrome__mark" aria-label={NAME.home} prefetch={false} {...homeIntent}>
          <Wordmark />
        </Link>

        <nav className="v3-chrome__nav" aria-label={NAME.primary}>
          {TOP_GROUPS.map((group) => (
            <V3ChromeDestination key={group.key} group={group} currentPath={path} live={live?.[group.key]} />
          ))}
        </nav>

        <div className="v3-chrome__actions">
          {/* Mobile bar: logo | Search | Call | hamburger (UXLIVE-13, visibility
              audit 2026-09-22). The 2026-09-06 lock gave the phone slot to
              Sign in; on all nine routes captured at 375 the first tappable
              phone was below the first screen (/sell y=1,479 to /cities/bend
              y=25,470), and a call is the highest-intent ask a brokerage gets.
              Under Matt's 2026-09-23 directive ("nothing is permanent") the
              phone takes the slot at every width and Sign in moves into the
              menu below 56.25rem (V3Chrome.css). Work with us lives in the dog
              floater (SITE-153), not the header (Matt 2026-09-21). The seller
              ask stays desktop. */}
          <V3ChromeSearch />
          {viewerReady && viewer ? (
            // Avatar only, no printed name (Matt 2026-09-21). The name still
            // reaches assistive tech through aria-label. viewer.avatar_url is
            // normalized in lib/auth/avatar.ts (metadata, then every OAuth
            // identity, Google first). Google's host 403s requests that
            // arrive with a Referer — referrerPolicy="no-referrer" is why
            // the picture used to work and then stopped. A signed-in user
            // with no picture (email/password) still gets a navy initial.
            <Link
              href={ACCOUNT_HOME?.href ?? '/account'}
              className="v3-chrome__account"
              data-clarity-mask="True"
              aria-current={isCurrentPath(path, ACCOUNT_HOME?.href ?? '/account') ? 'page' : undefined}
              aria-label={accountAriaLabel(viewer)}
            >
              {viewer.avatar_url && !avatarFailed ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={viewer.avatar_url}
                  alt=""
                  className="v3-chrome__avatar"
                  width={28}
                  height={28}
                  decoding="async"
                  referrerPolicy="no-referrer"
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <span className="v3-chrome__avatar v3-chrome__avatar--initial" aria-hidden="true">
                  {accountInitial(viewer)}
                </span>
              )}
            </Link>
          ) : SIGN_IN ? (
            <Link
              href={SIGN_IN.href}
              className="v3-chrome__signin"
              aria-current={isCurrentPath(path, SIGN_IN.href) ? 'page' : undefined}
            >
              {SIGN_IN.label}
            </Link>
          ) : null}

          <a
            href={`tel:${CONTACT.phoneDirectTel}`}
            className="v3-chrome__phone"
            aria-label={`Call Ryan Realty, ${CONTACT.phoneDirect}`}
          >
            <IconPhone />
            <span className="v3-chrome__phone-num">{CONTACT.phoneDirect}</span>
          </a>


          {chromeShowsSellerAsk(path) ? (
            <V3Button href={valuationHref(path)} className="v3-chrome__cta">
              {CTA.label}
            </V3Button>
          ) : null}

          <button
            ref={triggerRef}
            type="button"
            className="v3-chrome__menu-btn"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => {
              setOpenGroup(null)
              setOpenPath(path)
            }}
          >
            <IconMenu />
            <span className="v3-chrome__menu-word">{NAME.openMenu}</span>
          </button>
        </div>
      </div>

      {/* ALWAYS MOUNTED, closed with the `hidden` attribute rather than by
          conditional rendering, and that is an SEO decision as much as an
          accessibility one. This index is the only place a handful of
          destinations appear (the account pages, the communities the top bar
          trims), so rendering it only while the menu is open would drop them
          out of the HTML of every page on the site. `hidden` resolves to
          display:none, which takes the whole subtree out of the accessibility
          tree AND out of the tab order at once, so there is no
          invisible-but-focusable link and no aria-hidden wrapper around
          reachable controls. Going from display:none to display:flex is also
          what starts the entrance, so the animation still runs on open.
          aria-modal is on only while open: leaving it true on this hidden
          dialog blanks Sarah and the cookie gear (body:has). */}
      <div
        id={menuId}
        ref={overlayRef}
        hidden={menuHidden}
        className="v3-chrome__overlay"
        role="dialog"
        aria-modal={menuHidden ? undefined : true}
        aria-label={NAME.menu}
      >
        <div className="v3-chrome__overlay-bar">
          <Link href="/" className="v3-chrome__mark" aria-label={NAME.home} onClick={close}>
            <Wordmark />
          </Link>
          <button ref={closeRef} type="button" className="v3-chrome__close" onClick={close}>
            <IconClose />
            <span className="v3-chrome__menu-word">{NAME.closeMenu}</span>
          </button>
        </div>

        <nav className="v3-chrome__menu-nav" aria-label={NAME.sections}>
          {/* UX review F14, Matt 2026-10-25: the four jobs most visitors came
              for, as big buttons, before any list of pages. */}
          <ul className="v3-chrome__menu-actions">
            <li>
              <Link href="/homes-for-sale" onClick={close} className="v3-chrome__menu-action">
                Search homes
              </Link>
            </li>
            <li>
              <Link href={VALUATION_FORM.href} onClick={close} className="v3-chrome__menu-action">
                {VALUATION_FORM.label}
              </Link>
            </li>
            <li>
              <Link href="/book" onClick={close} className="v3-chrome__menu-action">
                Book a call
              </Link>
            </li>
            <li>
              <a
                href={`tel:${CONTACT.phoneDirectTel}`}
                onClick={close}
                className="v3-chrome__menu-action v3-chrome__menu-action--call"
              >
                <IconPhone />
                Call {CONTACT.phoneDirect}
              </a>
            </li>
          </ul>
          {NAV_GROUPS.filter(
            (group) => PRIMARY_BAR_KEYS.has(group.key) || group.key === ACCOUNT_KEY,
          ).map((group, index) => {
            const headingId = `${menuId}-group-${index}`
            const panelId = `${menuId}-panel-${index}`
            const expanded = openGroup === group.key
            const lg = live?.[group.key]
            const links =
              group.href && !group.links.some((link) => link.href === group.href)
                ? [{ href: group.href, label: 'Overview' }, ...group.links]
                : group.links
            const marks = chromeMegaColumnMarks(
              links.map((item) => item.href),
              CHROME_MARKS,
            )
            return (
              <div className="v3-chrome__menu-group" key={group.key}>
                {/* Closed panels stay in the HTML (`hidden`), so every door is
                    still crawlable and still out of the tab order. */}
                <button
                  type="button"
                  id={headingId}
                  className="v3-chrome__acc-btn"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => setOpenGroup(expanded ? null : group.key)}
                >
                  <span>{group.label}</span>
                  <IconChevron />
                </button>
                <div id={panelId} className="v3-chrome__acc-panel" hidden={!expanded}>
                  {lg && lg.facts.length === 0 && lg.eyebrow ? (
                    // W1: a group of bare counts (Places) says what they count.
                    <p className="v3-chrome__menu-live">{lg.eyebrow}</p>
                  ) : null}
                  {lg && lg.facts.length > 0 ? (
                    <p className="v3-chrome__menu-live">
                      {lg.facts.map((f, i) => (
                        <span key={`${f.figure} ${f.label}`}>
                          {i > 0 ? ' · ' : ''}
                          {f.after != null ? (
                            <>
                              {f.label} <strong>{f.figure}</strong>
                              {f.after}
                            </>
                          ) : (
                            <>
                              <strong>{f.figure}</strong> {f.label}
                            </>
                          )}
                        </span>
                      ))}
                    </p>
                  ) : null}
                  <ul className="v3-chrome__menu-list" aria-labelledby={headingId}>
                    {links.map((link) => {
                      const value = lg?.values?.[link.href]
                      return (
                        <li key={link.href}>
                          <Link href={link.href} onClick={close} className="v3-chrome__menu-link">
                            {marks?.[link.href] ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={marks[link.href]}
                                alt=""
                                className="v3-chrome__link-mark"
                                width={28}
                                height={28}
                                decoding="async"
                              />
                            ) : null}
                            <span>{link.label}</span>
                            {value ? <span className="v3-chrome__menu-value">{value}</span> : null}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
            )
          })}
        </nav>

        {/* The number leads the menu as the Call action, so the foot names the place only. */}
        <p className="v3-chrome__menu-foot">
          <span>Bend, Oregon</span>
        </p>
      </div>
    </header>
  )
}
